import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, Wifi, Globe, Languages } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { LANGS } from "@/lib/i18n";
import { UkGlyph } from "@/components/UkGlyph";

const NAV = [
  { to: "/prepare", key: "nav.prepare", tid: "prepare" },
  { to: "/live", key: "nav.live", tid: "live" },
  { to: "/report", key: "nav.report", tid: "report" },
  { to: "/packs", key: "nav.packs", tid: "packs" },
  { to: "/my-plan", key: "nav.myplan", tid: "my-plan" },
  { to: "/local", key: "nav.local", tid: "local" },
  { to: "/guides", key: "nav.guides", tid: "guides" },
];

const NATIONS = ["England", "Scotland", "Wales", "Northern Ireland"];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { nation, changeNation, lite, setLite, lang, changeLang, t } = useApp();
  const nav = useNavigate();

  const submit = (e) => { e.preventDefault(); if (q.trim()) { nav(`/guides?q=${encodeURIComponent(q.trim())}`); setOpen(false); } };

  return (
    <header data-print-hide className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
      <div className="container-x flex items-center justify-between h-16 gap-4">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-2.5 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-unionblue text-white ring-1 ring-inset ring-white/15" data-testid="uk-glyph-logo"><UkGlyph className="h-6 w-6" /></span>
          <span className="font-heading font-extrabold text-lg text-navy leading-none">UK Ready<span className="block text-[10px] font-mono font-medium tracking-widest text-crimson uppercase">{t("brand.tag")}</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.tid}`}
              className={({ isActive }) => `px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-navy text-white" : "text-navy hover:bg-slate-100"}`}>
              {t(n.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <form onSubmit={submit} className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input data-testid="header-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search.placeholder")}
              className="w-52 rounded-md border border-slate-300 bg-slate-50 pl-8 pr-3 py-2 text-sm focus:bg-white" aria-label="Search guidance" />
          </form>
          <div className="relative">
            <select data-testid="lang-select" value={lang} onChange={(e) => changeLang(e.target.value)}
              className="appearance-none rounded-md border border-slate-300 bg-white pl-7 pr-2 py-2 text-xs font-semibold text-navy" aria-label={t("lang.aria")}>
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <Languages className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          </div>
          <div className="relative">
            <select data-testid="nation-select" value={nation} onChange={(e) => changeNation(e.target.value)}
              className="appearance-none rounded-md border border-slate-300 bg-white pl-7 pr-2 py-2 text-xs font-semibold text-navy" aria-label={t("nation.aria")}>
              {NATIONS.map((n) => <option key={n}>{n}</option>)}
            </select>
            <Globe className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          </div>
          <button data-testid="low-data-toggle" onClick={() => setLite(!lite)} title={t("lowdata.title")}
            className={`touch rounded-md border px-2 ${lite ? "bg-navy text-white border-navy" : "border-slate-300 text-navy"}`}>
            <Wifi className="h-4 w-4" />
          </button>
        </div>

        <button data-testid="mobile-menu-btn" className="lg:hidden touch rounded-md border border-slate-300 px-2" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <form onSubmit={submit} className="p-3 border-b border-slate-100">
            <input data-testid="mobile-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search.placeholder")}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base" aria-label="Search" />
          </form>
          <nav className="flex flex-col p-2" aria-label="Mobile">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-3 py-3 rounded-md text-base font-semibold ${isActive ? "bg-navy text-white" : "text-navy"}`}>{t(n.key)}</NavLink>
            ))}
          </nav>
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-t border-slate-100">
            <select data-testid="mobile-lang-select" value={lang} onChange={(e) => changeLang(e.target.value)} className="rounded-md border border-slate-300 px-2 py-2 text-sm" aria-label={t("lang.aria")}>
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <select value={nation} onChange={(e) => changeNation(e.target.value)} className="rounded-md border border-slate-300 px-2 py-2 text-sm">
              {NATIONS.map((n) => <option key={n}>{n}</option>)}
            </select>
            <button onClick={() => setLite(!lite)} className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold">{lite ? t("lowdata.on") : t("lowdata.off")}</button>
          </div>
        </div>
      )}
    </header>
  );
}
